import { createSlice } from '@reduxjs/toolkit';
import FJAIVideoGeneratorData, {
    AI_VIDEO_GENERATION_MODEL_LIST,
    getModelRatios,
    getModelDurations,
    IMAGE_UPLOAD_MODES,
    MODEL_VALUE_MAP,
} from '../../config/dataConfig/FJAIVideoGeneratorData.js';
import FJConstant from '../../constants/FJConstant.js';
import { AI_VIDEO_DUB_USE_CREDIT } from './FJSliceAiVideoDub.js';
import FJVideoUtil from '../../util/FJVideoUtil.js';
import { VIDEO_EFFECT_STYLE_LIST } from '../../config/dataConfig/FJVideoEffectData.js';
import FJCommunityUtil from '../../util/FJCommunityUtil.js';
import FJUserSelectionStore, { SELECTION_KEYS } from '../../store/FJUserSelectionStore';
import FJAppConstant from '../../constants/FJAppConstant';
import { PUBLISH_TYPE } from '../../config/dataConfig/FJCommunityData.js';
import FJUtil from '../../util/FJUtil.js';

let persistedModelValue = FJUserSelectionStore.load(
    FJAppConstant.AI_VIDEO_GENERATOR,
    SELECTION_KEYS.CUR_MODEL,
    AI_VIDEO_GENERATION_MODEL_LIST[1].value,
    AI_VIDEO_GENERATION_MODEL_LIST,
);
const currentModelConfig =
    AI_VIDEO_GENERATION_MODEL_LIST.find(m => m.value === persistedModelValue) || AI_VIDEO_GENERATION_MODEL_LIST[1];

const defaultAvailableRatios = getModelRatios(currentModelConfig.value);
const defaultAvailableDurations = getModelDurations(currentModelConfig.value);
const defaultQuality = FJAIVideoGeneratorData.QUALITY_LIST[0].value;
const defaultDuration = defaultAvailableDurations[0].value;
const defaultCreditPrice = currentModelConfig?.creditPrice || 2;
const defaultTotalCredit = defaultCreditPrice * defaultDuration;
const defaultAvailableResolutions = currentModelConfig?.resolutions || [];
const defaultCurrentResolution = defaultAvailableResolutions[0]?.value || null;

const _initialState = {
    currentModel: currentModelConfig.value,
    availableResolutions: defaultAvailableResolutions,
    availableRatios: defaultAvailableRatios,
    availableDurations: defaultAvailableDurations,
    currentRatio: defaultAvailableRatios[0]?.name ?? null,
    currentQuality: defaultQuality,
    currentDuration: defaultDuration,
    tabIndex: FJVideoUtil.getAiGeneratorVideoTab(),
    tvTextValue: '',
    ivTextValue: '',
    uploadStatus: FJConstant.DEFAULT,

    inspireList: [],
    presetList: [],

    currentGenerateStatus: FJConstant.IDLE,
    currentTackId: null,
    generatingVideoNum: 0,
    isShowGeneratingVideoNum: false,
    ivFreeNum: 0,
    tvFreeNum: 0,
    evFreeNum: 0,
    sortType: 'all-videos',

    /** @deprecated  总消耗，请使用 modelConfig.creditPrice * currentDuration */
    totalCredit: defaultTotalCredit,
    /** @deprecated  积分价格，请使用 modelConfig.creditPriceLevels[currentResolution] 或 modelConfig.creditPrice */
    creditPrice: defaultCreditPrice,
    audioActive: false,
    aiSfxTotalCredit: 0, // 配音积分总消耗

    videoEffectStyleSelectId: 'character-swap',
    videoEffectUploadTwoImageInfo: [{}, {}],
    videoEffectUploadImageInfo: {},
    videoEffectUploadVideoInfo: {},
    videoEffectCredit: 15,
    evTextValue: '',
    videoEffectPresetList: [],
    dancePresetList: [],
    styleList: [],
    imageTypeIndex: 0,

    // 控制模型选择器是否显示
    isModelPickerVisible: false,

    // 图片上传模式相关状态
    imageMode:
        currentModelConfig?.mode === IMAGE_UPLOAD_MODES.START_END
            ? IMAGE_UPLOAD_MODES.SINGLE
            : currentModelConfig?.mode,
    originImage: {}, //@deprecated  单图模式图片
    uploadImages: new Array(4).fill(null), // 统一的图片数组，支持所有模式
    currentResolution: defaultCurrentResolution,
    curCharacterOrientation: 'video',
    motionControlImage: {},
    motionControlVideo: {},
    motionControlTextSwitch: false,

    // 当前提交的参数信息
    currentSubmitParams: {
        event_id: '',
        generation_type: '',
        model_type: '',
        prompt: '',
        duration: '',
        ratio: '',
        is_dubbing: '',
        audio_prompt: '',
        waterfall_ratio: '',
        source: '',
        current_image_mode: '',
        start_image: '',
        end_image: '',
        image: '',
        multi_image: [],
    },

    ivPrivacyStatus: FJCommunityUtil.getLocalStoragePrivacyStatus() || PUBLISH_TYPE.ONLY_PUBLIC,
    tvPrivacyStatus: FJCommunityUtil.getLocalStoragePrivacyStatus() || PUBLISH_TYPE.ONLY_PUBLIC,
    evPrivacyStatus: FJCommunityUtil.getLocalStoragePrivacyStatus() || PUBLISH_TYPE.ONLY_PUBLIC,
};

export const FJSliceAIVideoGenerator = createSlice({
    name: 'AIVideoGenerator',
    initialState: {
        ..._initialState,
    },
    reducers: {
        setSliceIVPrivacyStatus: (state, action) => {
            state.ivPrivacyStatus = action.payload;
            FJCommunityUtil.setLocalStoragePrivacyStatus(action.payload);
        },
        setSliceTVPrivacyStatus: (state, action) => {
            state.tvPrivacyStatus = action.payload;
            FJCommunityUtil.setLocalStoragePrivacyStatus(action.payload);
        },
        setSliceEVPrivacyStatus: (state, action) => {
            state.evPrivacyStatus = action.payload;
            FJCommunityUtil.setLocalStoragePrivacyStatus(action.payload);
        },
        setSliceModel: (state, action) => {
            const newModel = action.payload;
            state.currentModel = newModel?.value;
            state.availableRatios = newModel?.ratios || [];
            state.availableResolutions = newModel?.resolutions || [];

            //  REQUIREMENT: 检查当前比例是否被新模型支持，智能保持兼容选项
            const oldRatio = state.currentRatio;
            const oldResolution = state.currentResolution;
            const oldDuration = state.currentDuration;
            const isRatioSupported = state.availableRatios.some(ratio => ratio.value === oldRatio);
            const isResolutionSupported = state.availableResolutions.some(
                resolution => resolution.value === oldResolution,
            );
            state.currentRatio = isRatioSupported ? oldRatio : state.availableRatios[0]?.value || null;
            state.currentResolution = isResolutionSupported
                ? oldResolution
                : state.availableResolutions[0]?.value || null;
            const currentResolutionItem = state.availableResolutions.find(
                resolution => resolution.value === state.currentResolution,
            );
            state.availableDurations =
                state.currentResolution && currentResolutionItem?.supportDurations
                    ? newModel?.durations.filter(duration =>
                          currentResolutionItem.supportDurations.includes(duration.value),
                      )
                    : newModel?.durations || [];
            const isDurationSupported = state.availableDurations.some(duration => duration.value === oldDuration);
            state.currentDuration = isDurationSupported ? oldDuration : state.availableDurations[0]?.value || null;
            // 重新计算总积分
            const isMotionControl = newModel?.value === MODEL_VALUE_MAP.KLING_V26_MOTION_CONTROL;
            if (newModel?.customDurations && isMotionControl) {
                state.currentDuration = Math.ceil(state.motionControlVideo?.duration) || newModel?.defaultMinDuration;
            }
            state.creditPrice = newModel?.creditPriceLevels?.[state.currentResolution] || newModel?.creditPrice || 2;
            state.totalCredit = state.creditPrice * state.currentDuration;
            state.aiSfxTotalCredit = state.audioActive ? state.currentDuration * AI_VIDEO_DUB_USE_CREDIT : 0;
            // 重置图片上传模式
            // REQUIREMENT: 如果新模型是首尾帧模式，则默认显示单图模式
            state.imageMode =
                newModel?.mode === IMAGE_UPLOAD_MODES.START_END ? IMAGE_UPLOAD_MODES.SINGLE : newModel?.mode;

            // 音效
            if (!newModel?.sfxSupported) {
                state.audioActive = false;
                state.aiSfxTotalCredit = 0;
            }
        },
        setSliceRatio: (state, action) => {
            state.currentRatio = action.payload;
        },
        setSliceQuality: (state, action) => {
            state.currentQuality = action.payload;
            state.creditPrice = action.payload === 1 ? 2 : 7;
            state.totalCredit = state.creditPrice * state.currentDuration;
        },
        setSliceDuration: (state, action) => {
            state.currentDuration = action.payload;
            state.totalCredit = state.creditPrice * action.payload;
            state.aiSfxTotalCredit = state.audioActive ? action.payload * AI_VIDEO_DUB_USE_CREDIT : 0;
        },

        setSliceResolution: (state, action) => {
            state.currentResolution = action.payload;
            const oldDuration = state.currentDuration;
            const curModelConfig = AI_VIDEO_GENERATION_MODEL_LIST.find(model => model.value === state.currentModel);
            state.creditPrice =
                curModelConfig?.creditPriceLevels?.[state.currentResolution] || curModelConfig?.creditPrice || 2;
            const currentResolutionItem = state.availableResolutions.find(
                resolution => resolution.value === state.currentResolution,
            );
            state.availableDurations =
                state.currentResolution && currentResolutionItem?.supportDurations
                    ? curModelConfig?.durations.filter(duration =>
                          currentResolutionItem.supportDurations.includes(duration.value),
                      )
                    : curModelConfig?.durations || [];

            const isDurationSupported = state.availableDurations.some(duration => duration.value === oldDuration);
            state.currentDuration = isDurationSupported ? oldDuration : state.availableDurations[0]?.value || null;
            const isMotionControl = curModelConfig?.value === MODEL_VALUE_MAP.KLING_V26_MOTION_CONTROL;
            if (curModelConfig?.customDurations && isMotionControl) {
                state.currentDuration =
                    Math.ceil(state.motionControlVideo?.duration) || curModelConfig?.defaultMinDuration;
            }
            state.totalCredit = state.creditPrice * state.currentDuration;
            state.aiSfxTotalCredit = state.audioActive ? state.currentDuration * AI_VIDEO_DUB_USE_CREDIT : 0;
        },
        setSliceTabIndex: (state, action) => {
            state.tabIndex = action.payload;
        },
        setSliceTVTextValue: (state, action) => {
            state.tvTextValue = action.payload;
        },
        setSliceIVTextValue: (state, action) => {
            state.ivTextValue = action.payload;
        },
        setSliceEVTextValue: (state, action) => {
            state.evTextValue = action.payload;
        },
        setSliceOriginImage: (state, action) => {
            state.originImage = action.payload;
        },
        setSliceUploadStatus: (state, action) => {
            state.uploadStatus = action.payload;
        },
        setSliceInspireList: (state, action) => {
            state.inspireList = action.payload;
        },
        setSlicePresetList: (state, action) => {
            state.presetList = action.payload;
        },
        setSliceCurrentGenerateStatus: (state, action) => {
            state.currentGenerateStatus = action.payload;
        },
        setSliceCurrentTackId: (state, action) => {
            state.currentTackId = action.payload;
        },
        setSliceGeneratingVideoNum: (state, action) => {
            state.generatingVideoNum = action.payload;
        },
        setSliceIsShowGeneratingVideoNum: (state, action) => {
            state.isShowGeneratingVideoNum = action.payload;
        },

        setSliceIVFreeNumCreationAction: (state, action) => {
            state.ivFreeNum = action.payload;
        },

        setSliceTVFreeNumCreationAction: (state, action) => {
            state.tvFreeNum = action.payload;
        },

        setSliceSortType: (state, action) => {
            state.sortType = action.payload;
        },

        setSliceAudioActive: (state, action) => {
            state.audioActive = action.payload;
            state.aiSfxTotalCredit = action.payload ? AI_VIDEO_DUB_USE_CREDIT * state.currentDuration : 0;
        },
        setSliceVideoEffectPresetList: (state, action) => {
            state.videoEffectPresetList = action.payload;
        },
        setSliceStyleList: (state, action) => {
            state.styleList = action.payload;
        },
        setSliceVideoEffectImageTypeIndex: (state, action) => {
            state.imageTypeIndex = action.payload;
        },
        setVideoEffectUploadImageInfo: (state, action) => {
            state.videoEffectUploadImageInfo = action.payload;
        },
        setSliceVideoEffectUploadTwoImageInfo: (state, action) => {
            state.videoEffectUploadTwoImageInfo = action.payload;
        },
        setVideoEffectStyleSelectId: (state, action) => {
            state.videoEffectStyleSelectId = action.payload;
            const videoStyleList = ['dancing', 'mimic-motion', 'character-swap'];
            const videoEffectStyleItem = VIDEO_EFFECT_STYLE_LIST.find(item => item.key === action.payload);
            const duration = Math.ceil(state.videoEffectUploadVideoInfo?.duration) || 1;
            if (videoStyleList.includes(action.payload)) {
                state.videoEffectCredit = duration * (videoEffectStyleItem?.creditPrice || 15); // 默认15
            } else {
                state.videoEffectCredit = videoEffectStyleItem?.creditPrice || 40; // 默认40
            }
        },
        setSliceModelPickerVisible: (state, action) => {
            state.isModelPickerVisible = action.payload;
        },
        setSliceImageMode: (state, action) => {
            state.imageMode = action.payload;
            const curModelConfig = AI_VIDEO_GENERATION_MODEL_LIST.find(model => model.value === state.currentModel);
            if (curModelConfig?.startEndSupportDurations && action.payload === IMAGE_UPLOAD_MODES.START_END) {
                const oldDuration = state.currentDuration;
                state.availableDurations = curModelConfig.durations.filter(duration =>
                    curModelConfig?.startEndSupportDurations.includes(duration.value),
                );
                const isDurationSupported = state.availableDurations.some(duration => duration.value === oldDuration);
                state.currentDuration = isDurationSupported ? oldDuration : state.availableDurations[0]?.value || null;
                state.totalCredit = state.creditPrice * state.currentDuration;
                state.aiSfxTotalCredit = state.audioActive ? state.currentDuration * AI_VIDEO_DUB_USE_CREDIT : 0;
            }
        },
        // 统一的图片数组操作
        updateSliceUploadImage: (state, action) => {
            const { index, imageData } = action.payload;
            if (index >= 0 && index < state.uploadImages.length) {
                state.uploadImages[index] = imageData;
            }
        },
        removeSliceUploadImage: (state, action) => {
            const index = action.payload;
            if (index >= 0 && index < state.uploadImages.length) {
                state.uploadImages[index] = null;
            }
        },
        // 更新当前提交参数
        setSliceCurrentSubmitParams: (state, action) => {
            state.currentSubmitParams = { ...state.currentSubmitParams, ...action.payload };
        },
        // 重置所有状态
        setSliceReset: state => {
            const ignoreKeys = ['inspireList', 'presetList', 'videoEffectPresetList', 'styleList'];
            for (const stateKey in _initialState) {
                if (!ignoreKeys.includes(stateKey)) {
                    state[stateKey] = _initialState[stateKey];
                }
            }
        },
        setSliceDancePresetList: (state, action) => {
            state.dancePresetList = action.payload;
        },
        setSliceVideoEffectUploadVideoInfo: (state, action) => {
            state.videoEffectUploadVideoInfo = action.payload;
            const duration = Math.ceil(action.payload?.duration) || 1;
            state.videoEffectCredit = duration * 15;
        },
        setSliceVideoEffectCredit: (state, action) => {
            state.videoEffectCredit = action.payload;
        },
        setSliceMotionControlImage: (state, action) => {
            state.motionControlImage = action.payload;
        },
        setSliceMotionControlVideo: (state, action) => {
            state.motionControlVideo = action.payload;
            const duration = Math.ceil(action.payload?.duration) || 3;
            state.currentDuration = duration;
            state.totalCredit = state.creditPrice * state.currentDuration;
        },
        setSliceCurCharacterOrientation: (state, action) => {
            state.curCharacterOrientation = action.payload;
        },
        setSliceMotionControlTextSwitch: (state, action) => {
            state.motionControlTextSwitch = action.payload;
        },
    },
});

export const {
    setSliceModel,
    setSliceRatio,
    setSliceQuality,
    setSliceDuration,
    setSliceTabIndex,
    setSliceTVTextValue,
    setSliceIVTextValue,
    setSliceOriginImage,
    setSliceUploadStatus,
    setSliceInspireList,
    setSlicePresetList,
    setSliceCurrentGenerateStatus,
    setSliceCurrentTackId,
    setSliceGeneratingVideoNum,
    setSliceIsShowGeneratingVideoNum,
    setSliceIVFreeNumCreationAction,
    setSliceTVFreeNumCreationAction,
    setSliceSortType,
    setSliceAudioActive,
    setVideoEffectStyleSelectId,
    setVideoEffectUploadImageInfo,
    setSliceEVTextValue,
    setSliceVideoEffectPresetList,
    setSliceStyleList,
    setSliceVideoEffectUploadTwoImageInfo,
    setSliceVideoEffectImageTypeIndex,
    setSliceModelPickerVisible,
    setSliceImageMode,
    setSliceCurrentSubmitParams,
    setSliceResolution,
    setSliceDancePresetList,
    setSliceVideoEffectUploadVideoInfo,
    setSliceVideoEffectCredit,
    // 统一图片数组的 actions
    updateSliceUploadImage,
    removeSliceUploadImage,
    setSliceReset,
    setSliceIVPrivacyStatus,
    setSliceTVPrivacyStatus,
    setSliceEVPrivacyStatus,
    setSliceCurCharacterOrientation,
    setSliceMotionControlImage,
    setSliceMotionControlVideo,
    setSliceMotionControlTextSwitch,
} = FJSliceAIVideoGenerator.actions;

export default FJSliceAIVideoGenerator.reducer;
